/* eslint-disable css-modules/no-unused-class */
import { Fragment, memo, useCallback, useEffect, useMemo } from "react";
import { AdUnitsKeys, units } from "./config/units";
import { useDeviceType } from "@/common/hooks/useDeviceType";
import { deviceTypeEnum } from "@/common/utils/device";
import { isDisableAds } from "./utils";
import { useGetAdSlots } from "@/common/queries/queries";
import classes from "./AdUnit.module.scss";

const RETRY_LIMIT = 5;
const TIMEOUT = 1000;

export enum AdUnitTypeEnum {
  MOBILE = "Mobile",
  DESKTOP = "Desktop"
}

interface AdUnitProps {
  name: AdUnitsKeys;
  type: AdUnitTypeEnum;
}

const AdUnit = memo(({ name, type }: AdUnitProps) => {
  const isDesktop = useDeviceType() === deviceTypeEnum.DESKTOP;
  const { data: { slots = [] } = {}, isLoading: slotsIsLoading, isError: slotsError } = useGetAdSlots();

  const code = units[name];
  const id = code.display ?? `sp_${code.sid}`;

  const isDisabledSlot = useMemo(() => {
    if (slotsIsLoading) {
      return true;
    }

    if (slotsError) {
      return false;
    }

    if (!slots.length) {
      return false;
    }

    return slots.indexOf(name) === -1;
  }, [slots, slotsIsLoading, slotsError, name]);

  const addCode = useCallback((iteration: number) => {
    if (iteration > RETRY_LIMIT) {
      return;
    }

    if (!window?.goCtrl?.add) {
      console.debug("[ads] goCtrl instance not found");
      setTimeout(() => {
        addCode(iteration + 1);
      }, TIMEOUT);
      return;
    }

    const { ...goCtrlCode } = code;
    goCtrlCode.display = id;
    goCtrlCode.style = undefined;

    console.debug("[ads] goCtrl is ready.");

    window.goCtrl.add(goCtrlCode);
  }, [code, id]);

  useEffect(() => {
    if (isDisableAds() || isDisabledSlot) {
      return;
    }

    addCode(0);
  }, [addCode, isDisabledSlot]);

  if (isDisabledSlot) {
    return null;
  }

  const containerClass = `bannerWrapper ${classes.bannerWrapper} ${code.className ? classes[code.className] : ""}`.trim();

  return (
    <Fragment>
      {((isDesktop && type === AdUnitTypeEnum.DESKTOP) || (!isDesktop && type === AdUnitTypeEnum.MOBILE)) && (
        <div className={containerClass} style={code.style}>
          <div className={classes.adBox} id={id} />
        </div>
      )}
    </Fragment>
  );
});

export default AdUnit;
